import { FilterDataTyped } from '@nexelem/filters';
import { useGraphneAPI } from '@nexelem/graphne-api';
import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { useCustomToast } from '@nexelem/snackbar';
import { Trans } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { DrawerFiltersData } from '../../../../../../components/organisms/ProductionPlans/components/TasksDrawer/components/TasksDrawerFilters';
import { filtersMapper } from '../../../../../../components/organisms/ProductionPlans/components/TasksDrawer/mappers';
import { useSimulationId } from '../../../../../../components/organisms/ProductionPlans/GanttSimulation/hooks';
import { useSelectAllStore } from '../../../../../../components/organisms/ToDoList/store/useSelectAllStore';
import { useCustomTranslation } from '../../../../../../hooks';

type AutoPlanModalProps = {
  onSuccess: () => void;
  checkedTasksId: number[];
  totalTasks: number;
  filters: FilterDataTyped<DrawerFiltersData>;
};

export const AutoPlanModal = ({ onSuccess, checkedTasksId, totalTasks, filters }: AutoPlanModalProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.autoPlanModal');
  const { id } = useSimulationId();

  const selectAll = useSelectAllStore((state) => state.selectAll);
  const correctCount = selectAll ? totalTasks - checkedTasksId.length : checkedTasksId.length;

  const tasksCount = tWrapper('tasksCount.tasks', {
    count: correctCount,
  });
  const successMessageTasksCount = tWrapper('successMessage.tasksCount.tasks', { count: correctCount });

  const graphneApi = useGraphneAPI();
  const displayToast = useCustomToast();

  /**
   * how endpoint postGraphneSimulationSimulationidAutoplan works:
   * when selectAll is false => taskIds are the ones that will go to autoplan
   * when selectAll is true => then taskIds are the ones that are excluded from autoplan
   */

  const onSave = useMutation({
    mutationFn: (ids: number[]) =>
      graphneApi.postGraphneSimulationSimulationidAutoplan({
        autoPlanRequest: {
          selectAll,
          taskIds: ids,
          pullPlanning: true,
          filter: {
            data: filtersMapper(filters),
          },
        },
        simulationId: String(id),
      }),
    onSuccess: () => {
      closeAutoPlanModal();
      onSuccess();
      displayToast({
        toastType: 'success',
        message: (
          <Trans components={{ strong: <strong /> }}>
            {tWrapper('successMessage.message', { tasksCount: successMessageTasksCount })}
          </Trans>
        ),
      });
    },
    onError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('errorMessage'),
      });
    },
  });

  const onSubmit = () => {
    onSave.mutate(checkedTasksId);
  };

  return (
    <StaticModal maxWidth="lg" onClose={closeAutoPlanModal}>
      <StaticModal.Header withCloseButton>{tWrapper('title')}</StaticModal.Header>
      <StaticModal.Content>
        <div className="flex flex-col gap-6">
          <span className="text-s18">
            <Trans components={{ strong: <strong /> }}>{tWrapper('descriptionSubtitle', { tasksCount })}</Trans>
          </span>
          {/* TODO: Add link / Wait for BE to implement that */}
          {/*
           <StyledLink type="button" to={'#'} >
           <span className'text-s18' >
           {tWrapper('descriptionLink')}
           </span>
           </StyledLink>
           */}
          <span className="text-s18">{tWrapper('descriptionLine2')}</span>
        </div>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button variant="outlined" onClick={closeAutoPlanModal}>
          {tWrapper('cancel')}
        </Button>
        <Button isLoading={onSave.isPending} variant="contained" onClick={onSubmit}>
          {tWrapper('confirmButton', { taskAmount: correctCount })}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openAutoPlanModal, closeAutoPlanModal } = createModal(AutoPlanModal, {
  name: 'AutoPlanModal',
});
